import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Box, CircularProgress, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { toast } from 'react-toastify';

import { ContentFileCard } from '~/views/private/overview/ContentEditor/ContentFileCard';
import { Search } from '~/views/private/overview/ContentEditor/icons/Search';
import {
  buildAwsContentUrlSortableIds,
  defaultSubsectionForTab,
  deleteExplorer360ScanFolder,
  deleteExplorerContentFile,
  downloadAwsContentFile,
  downloadExplorer360ScanFolder,
  FLOOR_PLAN_SUBSECTION_TABS,
  galleryMediaExt,
  getAwsFileAccept,
  getContentUnitListLabel,
  getFloorPlanListLabel,
  isAwsGalleryVideoExt,
  loadAwsUrls,
  loadExplorerContentLists,
  matchesContentSearch,
  pickNonEmptyString,
  resolveExplorer360ScanFolders,
  saveAwsContentUrlsOrder,
  UNIT_SUBSECTION_TABS,
  uploadExplorerContentFiles,
} from '~/views/private/overview/ContentEditor/explorerContentUtils';

const TOAST_OPTIONS = {
  position: toast.POSITION.TOP_RIGHT,
  autoClose: 3000,
};

const useStyles = makeStyles(() => ({
  root: {
    minHeight: 0,
    width: '100%',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    background: 'var(--surface-default, #FFFFFF)',
  },
  left: {
    minHeight: 0,
    height: '100%',
    width: 280,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid var(--border-default, #171C261A)',
    background: 'var(--surface-default, #FFFFFF)',
  },
  leftTop: {
    boxSizing: 'border-box',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    borderBottom: '1px solid var(--border-default, #171C261A)',
  },
  tabGroup: {
    width: '100%',
    display: 'flex',
    background: 'var(--surface-tint-weak, #171C260D)',
    borderRadius: 8,
  },
  tabButton: {
    flex: '1 1 0',
    minWidth: 0,
    height: 32,
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line)',
  },
  tabButtonSelected: {
    background: 'var(--surface-default, #FFFFFF)',
    boxShadow: 'inset 0 0 0 1px var(--border-rude, #2C313A)',
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: 8,
    color: 'var(--content-primary-weak)',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    height: 32,
    boxSizing: 'border-box',
    padding: '6px 8px 6px 28px',
    borderRadius: 999,
    border: '1px solid var(--border-default, #171C261A)',
    background: 'var(--surface-default, #FFFFFF)',
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontWeight: 400,
    outline: 'none',
    '&::placeholder': {
      color: 'var(--content-primary-weak)',
    },
  },
  results: {
    flexGrow: 1,
    boxSizing: 'border-box',
    padding: 12,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflow: 'auto',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    flexGrow: 1,
    minHeight: 120,
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
  },
  itemButton: {
    width: '100%',
    minHeight: 36,
    padding: '6px 8px',
    border: 'none',
    borderRadius: 8,
    background: 'var(--surface-tint-weak, #171C260D)',
    cursor: 'pointer',
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-md)',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-md-line)',
    textAlign: 'center',
    overflowWrap: 'anywhere',
  },
  itemButtonSelected: {
    background: 'var(--surface-default, #FFFFFF)',
    boxShadow: 'inset 0 0 0 1px var(--border-rude, #2C313A)',
  },
  right: {
    flexGrow: 1,
    boxSizing: 'border-box',
    padding: 12,
    height: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    overflow: 'auto',
  },
  section: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--surface-default, #ffffff)',
  },
  row: {
    boxSizing: 'border-box',
    width: '100%',
    padding: '0 12px',
    display: 'flex',
  },
  rowInner: {
    boxSizing: 'border-box',
    width: '100%',
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  borderBottom: {
    borderBottom: '1px solid var(--border-default, #171C261A)',
  },
  borderTop: {
    borderTop: '1px solid var(--border-default, #171C261A)',
  },
  title: {
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-lg)',
    fontWeight: 700,
    lineHeight: 'var(--typography-line-height-lg-line)',
  },
  subsectionGroup: {
    width: 'fit-content',
    maxWidth: '100%',
    display: 'flex',
    background: 'var(--surface-tint-weak, #171C260D)',
    borderRadius: 8,
  },
  subsectionButton: {
    flex: '0 0 auto',
    height: 32,
    padding: '6px 8px',
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontWeight: 500,
  },
  gallery: {
    minHeight: 100,
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  uploadZone: {
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 120,
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    borderRadius: 8,
    border: '2px dashed var(--border-default, #171C261A)',
    background: 'var(--surface-tint-weak, #171C260D)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-tint-medium, #171C261F)',
      borderColor: 'var(--border-strong, #171C2633)',
    },
  },
  uploadZoneBusy: {
    pointerEvents: 'none',
    opacity: 0.55,
  },
  hiddenInput: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  uploadPrompt: {
    margin: 0,
    textAlign: 'center',
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-md)',
    fontWeight: 500,
    cursor: 'pointer',
  },
  browseWrap: {
    position: 'relative',
    display: 'inline-flex',
  },
  browseButton: {
    height: 36,
    padding: '0 12px',
    border: 'none',
    borderRadius: 8,
    background: 'var(--surface-default, #FFFFFF)',
    boxShadow: 'inset 0 0 0 1px var(--border-default, #171C261A)',
    cursor: 'pointer',
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontWeight: 500,
  },
  browseDim: {
    position: 'absolute',
    inset: 0,
    borderRadius: 8,
    background: 'var(--surface-tint-medium, #171C261F)',
    pointerEvents: 'none',
  },
  browseLoader: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  },
  fileList: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 216px)',
    gap: 12,
    boxSizing: 'border-box',
    padding: 12,
    justifyContent: 'start',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: 88,
    padding: '32px 16px',
    boxSizing: 'border-box',
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
  },
}));

export const ContentEditor = ({ explorer3dId, updatedBy }) => {
  const classes = useStyles();
  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [tab, setTab] = useState('floorPlans');
  const [subsection, setSubsection] = useState('plans');
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [units, setUnits] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [awsUrls, setAwsUrls] = useState([]);
  const [orderedAwsUrls, setOrderedAwsUrls] = useState([]);
  const [scanFolders, setScanFolders] = useState([]);
  const [awsLoading, setAwsLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [reorderBusy, setReorderBusy] = useState(false);
  const [downloadRowKey, setDownloadRowKey] = useState(null);
  const fileInputRef = useRef(null);

  const entityType = tab === 'floorPlans' ? 'floorplans' : 'units';
  const is360ScansTab = subsection === '360s';
  const subsectionTabs =
    tab === 'floorPlans' ? FLOOR_PLAN_SUBSECTION_TABS : UNIT_SUBSECTION_TABS;

  useEffect(() => {
    if (!explorer3dId) return undefined;

    let cancelled = false;
    setListsLoading(true);

    loadExplorerContentLists(explorer3dId)
      .then(({ units: nextUnits, unitTypes: nextUnitTypes }) => {
        if (cancelled) return;
        setUnits(nextUnits);
        setUnitTypes(nextUnitTypes);
      })
      .catch(error => {
        console.error(
          'Failed to load explorer content units / unit types',
          error,
        );
        toast.error('Failed to load floorplans and units', TOAST_OPTIONS);
        if (!cancelled) {
          setUnits([]);
          setUnitTypes([]);
        }
      })
      .finally(() => {
        if (!cancelled) setListsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [explorer3dId]);

  const floorPlanEntries = useMemo(
    () =>
      unitTypes
        .map(row => {
          const id = pickNonEmptyString(row && row.id);
          if (!id) return null;
          return { id, label: getFloorPlanListLabel(row) };
        })
        .filter(Boolean),
    [unitTypes],
  );

  const unitListEntries = useMemo(
    () =>
      units
        .map((row, index) => {
          const id = pickNonEmptyString(row && row.id) || `__unit_${index}`;
          const label = getContentUnitListLabel(row);
          return label ? { id, label } : null;
        })
        .filter(Boolean),
    [units],
  );

  const visibleEntries = useMemo(() => {
    const entries = tab === 'floorPlans' ? floorPlanEntries : unitListEntries;
    return entries.filter(entry =>
      matchesContentSearch(entry.label, searchQuery),
    );
  }, [tab, floorPlanEntries, unitListEntries, searchQuery]);

  const listIsEmpty =
    tab === 'floorPlans'
      ? floorPlanEntries.length === 0
      : unitListEntries.length === 0;
  const searchHasNoMatches = !listIsEmpty && visibleEntries.length === 0;

  const selectedLabel = useMemo(() => {
    if (!selectedId) return null;
    const entries = tab === 'floorPlans' ? floorPlanEntries : unitListEntries;
    const match = entries.find(entry => entry.id === selectedId);
    return match ? match.label : null;
  }, [selectedId, tab, floorPlanEntries, unitListEntries]);

  const panelReady = Boolean(selectedId && selectedLabel);

  const handleTabChange = nextTab => {
    setTab(nextTab);
    setSubsection(defaultSubsectionForTab(nextTab));
    setSelectedId(null);
    setSearchQuery('');
  };

  const reloadAwsContent = useCallback(async () => {
    if (!explorer3dId || !selectedId) {
      setAwsUrls([]);
      setScanFolders([]);
      return;
    }

    const urls = await loadAwsUrls(selectedId, subsection);
    if (subsection !== '360s') {
      setScanFolders([]);
      setAwsUrls(urls);
      return;
    }

    const folders = await resolveExplorer360ScanFolders({
      explorer3dId,
      entityId: selectedId,
      entityType,
      storedItems: urls,
    });
    setAwsUrls(urls);
    setScanFolders(folders);
  }, [explorer3dId, selectedId, subsection, entityType]);

  useEffect(() => {
    if (!panelReady) {
      setAwsUrls([]);
      setScanFolders([]);
      setAwsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setAwsLoading(true);
    reloadAwsContent()
      .catch(error => {
        console.error('Failed to load AWS content links', error);
        if (!cancelled) {
          setAwsUrls([]);
          setScanFolders([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAwsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [panelReady, reloadAwsContent]);

  useEffect(() => {
    setOrderedAwsUrls(awsUrls);
  }, [awsUrls]);

  const sortableIds = useMemo(
    () => buildAwsContentUrlSortableIds(orderedAwsUrls),
    [orderedAwsUrls],
  );

  const runUpload = useCallback(
    async rawFiles => {
      if (!explorer3dId || !selectedId || !rawFiles.length) return;
      setUploadBusy(true);
      try {
        const urls = await uploadExplorerContentFiles({
          files: rawFiles,
          explorer3dId,
          entityType,
          entityId: selectedId,
          contentType: subsection,
          updatedBy,
        });
        setAwsUrls(urls);
        toast.success('Files successfully uploaded!', TOAST_OPTIONS);
      } catch (error) {
        console.error('AWS content upload failed', error);
        toast.error('Failed to upload files. Please try again.', TOAST_OPTIONS);
      } finally {
        setUploadBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [explorer3dId, selectedId, entityType, subsection, updatedBy],
  );

  const handleDownload = useCallback(async (fileUrl, fileName, rowKey) => {
    setDownloadRowKey(rowKey);
    try {
      await downloadAwsContentFile(
        fileUrl,
        pickNonEmptyString(fileName) || 'download',
      );
    } catch (error) {
      console.error('AWS content download failed', error);
      toast.error('Failed to download file. Please try again.', TOAST_OPTIONS);
    } finally {
      setDownloadRowKey(null);
    }
  }, []);

  const handle360Download = useCallback(async (folder, rowKey) => {
    setDownloadRowKey(rowKey);
    try {
      await downloadExplorer360ScanFolder(folder);
    } catch (error) {
      console.error('360 scan download failed', error);
      toast.error(
        'Failed to download 360 scan. Please try again.',
        TOAST_OPTIONS,
      );
    } finally {
      setDownloadRowKey(null);
    }
  }, []);

  const handleDelete = useCallback(
    async fileUrl => {
      setDeleteBusy(true);
      try {
        const { urls } = await deleteExplorerContentFile(
          fileUrl,
          entityType,
          updatedBy,
        );
        setAwsUrls(urls);
        toast.success('File successfully deleted!', TOAST_OPTIONS);
      } catch (error) {
        console.error('AWS content delete failed', error);
        toast.error('Failed to delete file. Please try again.', TOAST_OPTIONS);
      } finally {
        setDeleteBusy(false);
      }
    },
    [entityType, updatedBy],
  );

  const handle360Delete = useCallback(
    async folderUrl => {
      if (!explorer3dId || !selectedId) return;
      setDeleteBusy(true);
      try {
        const urls = await deleteExplorer360ScanFolder(folderUrl, updatedBy);
        const folders = await resolveExplorer360ScanFolders({
          explorer3dId,
          entityId: selectedId,
          entityType,
          storedItems: urls,
        });
        setAwsUrls(urls);
        setScanFolders(folders);
        toast.success('360 scan successfully deleted!', TOAST_OPTIONS);
      } catch (error) {
        console.error('360 scan delete failed', error);
        toast.error(
          'Failed to delete 360 scan. Please try again.',
          TOAST_OPTIONS,
        );
      } finally {
        setDeleteBusy(false);
      }
    },
    [explorer3dId, selectedId, entityType, updatedBy],
  );

  const handleReorder = useCallback(
    async (fromId, toId) => {
      if (!selectedId || reorderBusy) return;
      const oldIndex = sortableIds.indexOf(fromId);
      const newIndex = sortableIds.indexOf(toId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

      const previous = orderedAwsUrls;
      const nextState = arrayMove(previous, oldIndex, newIndex);
      setOrderedAwsUrls(nextState);

      setReorderBusy(true);
      try {
        await saveAwsContentUrlsOrder({
          entityId: selectedId,
          contentType: subsection,
          urls: nextState,
          isUnit: tab === 'units',
          updatedBy,
        });
        setAwsUrls(nextState);
      } catch (error) {
        console.error('AWS content reorder failed', error);
        toast.error(
          'Failed to reorder files. Please try again.',
          TOAST_OPTIONS,
        );
        setOrderedAwsUrls(previous);
      } finally {
        setReorderBusy(false);
      }
    },
    [
      selectedId,
      reorderBusy,
      sortableIds,
      orderedAwsUrls,
      subsection,
      tab,
      updatedBy,
    ],
  );

  const emptyFiles = is360ScansTab
    ? scanFolders.length === 0
    : orderedAwsUrls.length === 0;

  return (
    <Box className={classes.root}>
      <Box className={classes.left}>
        <Box className={classes.leftTop}>
          <Box className={classes.tabGroup}>
            <button
              type="button"
              className={clsx(
                classes.tabButton,
                tab === 'floorPlans' && classes.tabButtonSelected,
              )}
              onClick={() => handleTabChange('floorPlans')}
            >
              Floorplans
            </button>
            <button
              type="button"
              className={clsx(
                classes.tabButton,
                tab === 'units' && classes.tabButtonSelected,
              )}
              onClick={() => handleTabChange('units')}
            >
              Units
            </button>
          </Box>
          <Box className={classes.searchWrap}>
            <Search
              className={classes.searchIcon}
              color="var(--content-primary-weak)"
            />
            <input
              className={classes.searchInput}
              placeholder="Search"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
            />
          </Box>
        </Box>
        <Box className={classes.results}>
          {listsLoading && (
            <Box className={classes.centered}>
              <CircularProgress size={24} style={{ color: '#171C2666' }} />
            </Box>
          )}
          {!listsLoading && (listIsEmpty || searchHasNoMatches) && (
            <Box className={classes.centered}>
              {listIsEmpty ? 'No items' : 'No matches'}
            </Box>
          )}
          {!listsLoading &&
            !searchHasNoMatches &&
            visibleEntries.map(entry => (
              <button
                key={entry.id}
                type="button"
                className={clsx(
                  classes.itemButton,
                  selectedId === entry.id && classes.itemButtonSelected,
                )}
                onClick={() => setSelectedId(entry.id)}
              >
                {entry.label}
              </button>
            ))}
        </Box>
      </Box>

      <Box className={classes.right}>
        {panelReady && (
          <Box className={classes.section}>
            <Box className={classes.row}>
              <Box className={clsx(classes.rowInner, classes.borderBottom)}>
                <Box className={classes.title}>
                  Content - {tab === 'floorPlans' ? 'Floorplan' : 'Unit'}{' '}
                  {selectedLabel}
                </Box>
              </Box>
            </Box>
            <Box className={classes.row}>
              <Box className={classes.rowInner}>
                <Box className={classes.subsectionGroup}>
                  {subsectionTabs.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className={clsx(
                        classes.subsectionButton,
                        subsection === item.id && classes.tabButtonSelected,
                      )}
                      onClick={() => setSubsection(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box className={classes.row}>
              <Box className={clsx(classes.rowInner, classes.borderTop)}>
                <Box className={classes.gallery}>
                  {!is360ScansTab && (
                    <Box
                      className={clsx(
                        classes.uploadZone,
                        uploadBusy && classes.uploadZoneBusy,
                      )}
                      onDragOver={event => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onDrop={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (uploadBusy) return;
                        const { files } = event.dataTransfer;
                        if (files && files.length) {
                          runUpload(Array.from(files));
                        }
                      }}
                    >
                      <input
                        id="explorer-content-file-input"
                        ref={fileInputRef}
                        type="file"
                        className={classes.hiddenInput}
                        accept={getAwsFileAccept(subsection)}
                        multiple
                        onChange={event => {
                          const { files } = event.target;
                          if (files && files.length) {
                            runUpload(Array.from(files));
                          }
                        }}
                      />
                      <Box
                        className={classes.uploadPrompt}
                        onClick={() => {
                          if (!uploadBusy && fileInputRef.current) {
                            fileInputRef.current.click();
                          }
                        }}
                      >
                        Drop files here for upload
                      </Box>
                      <Box className={classes.browseWrap}>
                        <button
                          type="button"
                          className={classes.browseButton}
                          disabled={uploadBusy}
                          onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            if (!uploadBusy && fileInputRef.current) {
                              fileInputRef.current.click();
                            }
                          }}
                        >
                          Browse files
                        </button>
                        {uploadBusy && (
                          <>
                            <Box className={classes.browseDim} />
                            <Box className={classes.browseLoader}>
                              <CircularProgress
                                size={20}
                                style={{ color: '#171C2666' }}
                              />
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  )}

                  {awsLoading && (
                    <Box className={classes.centered}>
                      <CircularProgress
                        size={24}
                        style={{ color: '#171C2666' }}
                      />
                    </Box>
                  )}

                  {!awsLoading && emptyFiles && (
                    <Box className={classes.empty}>No files</Box>
                  )}

                  {!awsLoading && is360ScansTab && scanFolders.length > 0 && (
                    <DndContext
                      sensors={dndSensors}
                      collisionDetection={closestCenter}
                    >
                      <SortableContext
                        items={scanFolders.map(
                          folder => folder.folderUrl || folder.folderName,
                        )}
                        strategy={rectSortingStrategy}
                      >
                        <Box className={classes.fileList}>
                          {scanFolders.map(folder => {
                            const sortableId =
                              folder.folderUrl || folder.folderName;
                            const rowKey = `${folder.folderName}-${sortableId}`;
                            return (
                              <ContentFileCard
                                key={sortableId}
                                sortableId={sortableId}
                                url={folder.previewUrl}
                                name={folder.folderName}
                                showVideo={false}
                                downloading={downloadRowKey === rowKey}
                                reorderDisabled
                                deleteDisabled={
                                  deleteBusy || downloadRowKey === rowKey
                                }
                                downloadAriaLabel="Download all files"
                                onDownload={() =>
                                  handle360Download(folder, rowKey)
                                }
                                onDelete={() =>
                                  handle360Delete(folder.folderUrl)
                                }
                              />
                            );
                          })}
                        </Box>
                      </SortableContext>
                    </DndContext>
                  )}

                  {!awsLoading &&
                    !is360ScansTab &&
                    orderedAwsUrls.length > 0 && (
                      <DndContext
                        sensors={dndSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={event => {
                          const { active, over } = event;
                          if (!over || active.id === over.id) return;
                          handleReorder(String(active.id), String(over.id));
                        }}
                      >
                        <SortableContext
                          items={sortableIds}
                          strategy={rectSortingStrategy}
                        >
                          <Box className={classes.fileList}>
                            {orderedAwsUrls.map((item, index) => {
                              const url = pickNonEmptyString(item.link);
                              const name = pickNonEmptyString(item.fileName);
                              const sortableId = sortableIds[index];
                              const rowKey = `${name}-${String(
                                item.lastModified || '',
                              )}-${sortableId}`;
                              if (!url) return null;
                              const ext = galleryMediaExt(
                                name || undefined,
                                url,
                              );
                              return (
                                <ContentFileCard
                                  key={sortableId}
                                  sortableId={sortableId}
                                  url={url}
                                  name={name}
                                  showVideo={isAwsGalleryVideoExt(ext)}
                                  downloading={downloadRowKey === rowKey}
                                  reorderDisabled={reorderBusy}
                                  deleteDisabled={
                                    deleteBusy || uploadBusy || reorderBusy
                                  }
                                  onDownload={() =>
                                    handleDownload(url, name, rowKey)
                                  }
                                  onDelete={() => handleDelete(url)}
                                />
                              );
                            })}
                          </Box>
                        </SortableContext>
                      </DndContext>
                    )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

ContentEditor.propTypes = {
  explorer3dId: PropTypes.string,
  updatedBy: PropTypes.string,
};

ContentEditor.defaultProps = {
  explorer3dId: '',
  updatedBy: '',
};
